'use client';

import { Button, Icon, Input, InputWrapper, Logo, PasswordInput } from '../../components';
import { Paper } from '../../layout';
import type { FieldDefinition, LinkItem } from '../../types';
import { useState } from 'react';

export interface SignInProps {
  title: React.ReactNode;
  buttonTitle: string;
  forgotPasswordLink?: LinkItem;
  links?: boolean;
  registrationLink?: LinkItem;
  microsoftOAuthLink?: LinkItem;
  usernameField: Pick<FieldDefinition, 'label' | 'placeholder'>;
  passwordField: Pick<FieldDefinition, 'label' | 'placeholder'>;
  /** Optional CAPTCHA widget rendered above the submit button */
  captcha?: React.ReactNode;
  /** Disables the submit button (e.g. while a required CAPTCHA is unsolved) */
  submitDisabled?: boolean;
  onSubmit?: (username: string, password: string) => void;
  onMicrosoftOAuthClick?: () => void;
  /** Text for "or" separator, defaults to "nebo" */
  orText?: string;
  /** Text for "Don't have an account?", defaults to "Nemáte účet?" */
  noAccountText?: string;
  /** Aria label for show password button */
  ariaLabelShowPassword?: string;
  /** Aria label for hide password button */
  ariaLabelHidePassword?: string;
}

function MicrosoftIcon() {
  return (
    <svg
      className='w-5 h-5 shrink-0'
      viewBox='0 0 21 21'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <rect x='1' y='1' width='9' height='9' fill='#F25022' />
      <rect x='11' y='1' width='9' height='9' fill='#7FBA00' />
      <rect x='1' y='11' width='9' height='9' fill='#00A4EF' />
      <rect x='11' y='11' width='9' height='9' fill='#FFB900' />
    </svg>
  );
}

export function SignIn(props: SignInProps) {
  const {
    title,
    forgotPasswordLink,
    registrationLink,
    microsoftOAuthLink,
    buttonTitle,
    usernameField,
    passwordField,
    captcha,
    submitDisabled = false,
    links = false,
    onMicrosoftOAuthClick,
    orText = 'nebo',
    noAccountText = 'Nemáte účet?',
    ariaLabelShowPassword,
    ariaLabelHidePassword
  } = props;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (props.onSubmit) {
      props.onSubmit(username, password);
    } else {
      // In production, this would be logged to a monitoring service

      // eslint-disable-next-line no-console
      console.warn('onSubmit handler is not provided');
    }
  };
  return (
    <div className='flex flex-col items-center gap-xl w-full max-w-md px-md sm:px-0'>
      <Logo className='h-10'></Logo>
      <Paper className='flex flex-col items-center gap-sm shadow-lg border border-border-base rounded-xl w-full p-lg sm:p-xl'>
        <Paper.Title>{title}</Paper.Title>
        <Paper.Content className='w-full flex flex-col items-center gap-md'>
          <form className='w-full flex flex-col gap-md' onSubmit={handleSubmit} noValidate={false}>
            <InputWrapper
              className='w-full'
              label={usernameField.label}
              htmlFor='username'
              required
            >
              <Input
                leftContent={<Icon name='user'></Icon>}
                className='bg-background-input'
                placeholder={usernameField.placeholder}
                required
                id='username'
                name='username'
                type='email'
                value={username}
                onChange={e => setUsername(e.target.value)}
              />
            </InputWrapper>
            <InputWrapper
              className='w-full'
              label={passwordField.label}
              htmlFor='password'
              required
            >
              <PasswordInput
                leftContent={<Icon name='lock'></Icon>}
                className=' bg-background-input'
                placeholder={passwordField.placeholder}
                id='password'
                name='password'
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                ariaLabelShow={ariaLabelShowPassword}
                ariaLabelHide={ariaLabelHidePassword}
              />
            </InputWrapper>
            {links && forgotPasswordLink && (
              <a
                href={forgotPasswordLink.href}
                className='self-end text-sm text-foreground-muted hover:text-foreground'
              >
                {forgotPasswordLink.label}
              </a>
            )}
            {captcha}
            <Button
              type='submit'
              size='full'
              className='self-center px-md w-full! mt-lg'
              disabled={submitDisabled}
            >
              {buttonTitle}
            </Button>
          </form>
          {microsoftOAuthLink && (
            <div className='w-full flex flex-col items-center gap-sm mt-sm'>
              <span className='text-foreground-muted text-sm'>{orText}</span>
              <Button
                size='full'
                variant='default'
                className='w-full!'
                onClick={onMicrosoftOAuthClick}
              >
                <span className='flex items-center justify-center gap-sm'>
                  <MicrosoftIcon />
                  <span>{microsoftOAuthLink.label}</span>
                </span>
              </Button>
            </div>
          )}
          {links && registrationLink && (
            <footer className='flex justify-center gap-sm'>
              <span className='text-foreground-muted'>{noAccountText}</span>
              <a
                href={registrationLink.href}
                className='text-primary-base hover:text-primary-hover flex items-center gap-xs'
              >
                {registrationLink.label}
                <Icon name='chevron-down' rotate={270} className='text-primary-base' />
              </a>
            </footer>
          )}
        </Paper.Content>
      </Paper>
    </div>
  );
}
